import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import { useSession } from '@descope/react-sdk';
import { Box, Stack, Typography, useTheme } from '@mui/material';

import { useActivateUser, type UserDTO, useUpdateUser } from '@/api/generated';
import ColoredPersonPlusIcon from '@/assets/icons/colored-person-plus.svg?react';
import HierarchyChartFolderIcon from '@/assets/icons/hierarchy-chart-folder.svg?react';
import SquareBriefcaseColorIcon from '@/assets/icons/square-briefcase-color.svg?react';
import BackdropLoader from '@/components/BackdropLoader';
import DropdownSelect from '@/components/fields/DropdownSelect';
import Field from '@/components/fields/Field';
import TextInput from '@/components/fields/TextInput';
import Form from '@/components/Form/Form';
import LoadingButton from '@/components/LoadingButton';
import { GeneralStatusEnum } from '@/components/statuses/GeneralStatus/types';
import { ReadonlyProvider } from '@/hooks/useReadonly';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useSyncI18nWithPreferredLocale } from '@/hooks/useSyncI18nWithPreferredLocale';
import { EN_US_LOCALE } from '@/i18n';
import { ROUTER_PATHS } from '@/root/linker';
import { fadeInUp } from '@/theme/animations';
import { borders } from '@/theme/constants';
import typography from '@/theme/typography';
import { dateFormatOptions, timeZoneOptions } from '@/utils/dateTimeOptions';
import Logo from '@/views/Header/assets/logo.svg?react';

import LanguageSelector from './LanguageSelector';
import OnboardingFeatureCard from './OnboardingFeatureCard';

enum ONBOARDING_STEP {
  WELCOME = 'WELCOME',
  FORM = 'FORM',
  FINAL = 'FINAL'
}

interface OnboardingFormValues {
  language?: string;
  fullName?: string;
  jobTitle?: string;
  dateFormat?: string;
  preferredTimezone?: string;
}

interface OnboardingFlowProps {
  initialLanguage: string;
  currentUser: UserDTO;
}

const OnboardingFlow = ({ initialLanguage, currentUser }: OnboardingFlowProps) => {
  const { t, i18n } = useTranslation(['onboarding', 'common']);
  const { spacing } = useTheme();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<ONBOARDING_STEP>(ONBOARDING_STEP.WELCOME);
  const form = useForm<OnboardingFormValues>({
    mode: 'onChange',
    defaultValues: {
      language: initialLanguage,
      fullName: '',
      jobTitle: '',
      dateFormat: '',
      preferredTimezone: ''
    }
  });

  useEffect(() => {
    void i18n.changeLanguage(initialLanguage);
  }, [i18n, initialLanguage]);

  const { mutateAsync: updateUser, isLoading: isUpdatingUser } = useUpdateUser();
  const { mutateAsync: activateUser, isLoading: isActivatingUser } = useActivateUser();
  const { showMessage } = useSnackbar();

  useEffect(() => {
    if (currentStep === ONBOARDING_STEP.WELCOME && currentUser.id) {
      const timer = setTimeout(() => {
        setCurrentStep(ONBOARDING_STEP.FORM);
      }, 400);

      return () => clearTimeout(timer);
    }
  }, [currentStep, currentUser.id]);

  const handleGoHome = useCallback(async () => {
    try {
      await activateUser({ userId: currentUser.id });
      navigate(ROUTER_PATHS.HOME);
    } catch (error) {
      showMessage(t('common:error.somethingWentWrong'));
    }
  }, [navigate, currentUser.id, activateUser, showMessage, t]);

  const handleSubmit = useCallback(
    async (data: OnboardingFormValues) => {
      try {
        await updateUser({
          userId: currentUser.id,
          data: {
            fullName: data.fullName,
            jobTitle: data.jobTitle,
            preferredDateFormat: data.dateFormat,
            preferredTimezone: data.preferredTimezone,
            preferredLocale: data.language ?? EN_US_LOCALE
          }
        });

        setCurrentStep(ONBOARDING_STEP.FINAL);
      } catch (error) {
        showMessage(t('common:error.somethingWentWrong'));
      }
    },
    [currentUser.id, updateUser, showMessage, t]
  );

  return (
    <Stack minHeight="100vh" justifyContent="center" alignItems="center" sx={{ bgcolor: 'primary.50' }}>
      <Form form={form} onSubmit={handleSubmit}>
        <Stack
          sx={{
            width: 775,
            minHeight: '700px',
            borderRadius: 2,
            p: 4,
            m: 4,
            bgcolor: 'background.default'
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="start" sx={{ mb: 4 }}>
            <Box>
              <Logo />
            </Box>
            <ReadonlyProvider readonly={currentStep === ONBOARDING_STEP.FINAL}>
              <LanguageSelector />
            </ReadonlyProvider>
          </Stack>
          <Stack gap={3} width="100%">
            <Stack
              key="welcome"
              gap={3}
              sx={{
                animation: currentStep === ONBOARDING_STEP.WELCOME ? fadeInUp : undefined
              }}
            >
              <Typography variant="h2" fontWeight={typography.fontWeightMedium} color="text.primary" mb={2}>
                {t('onboarding.greeting')}
              </Typography>
              <Typography variant="body1" color="text.primary">
                {t('onboarding.welcomeMessage1')}
              </Typography>
              <Typography variant="body1" color="text.primary">
                {t('onboarding.welcomeMessage2')}
              </Typography>
            </Stack>
            {[ONBOARDING_STEP.FORM, ONBOARDING_STEP.FINAL].includes(currentStep) && (
              <Stack
                key="form"
                gap={3}
                sx={{
                  animation: currentStep === ONBOARDING_STEP.FORM ? fadeInUp : undefined
                }}
              >
                <Typography variant="body1" color="text.primary">
                  {t('onboarding.formTitle')}
                </Typography>
                <ReadonlyProvider readonly={currentStep !== ONBOARDING_STEP.FORM}>
                  <Box
                    sx={{
                      bgcolor: 'grey.100',
                      p: spacing(3),
                      borderRadius: 1
                    }}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: spacing(2, 1.5),
                        '& .MuiInputBase-root': { minHeight: spacing(5) },
                        '& .MuiFormControl-root': { px: 0, pt: 0 },
                        '& .DropdownSelect-control': { minHeight: spacing(5) },
                        '& .DropdownSelect-control[aria-disabled="true"]': {
                          bgcolor: 'grey.200',
                          border: borders.half,
                          borderColor: 'grey.600',
                          '& .DropdownSelect-optionText .MuiTypography-root': {
                            color: 'grey.500'
                          }
                        }
                      }}
                    >
                      <Field
                        component={TextInput}
                        name="fullName"
                        label={t('onboarding.fields.fullName')}
                        withFormControl
                        rules={{ required: true }}
                        innerProps={{ markAsRequired: true }}
                      />
                      <Field
                        component={TextInput}
                        name="jobTitle"
                        label={t('onboarding.fields.jobTitle')}
                        withFormControl
                        rules={{ required: true }}
                        innerProps={{ markAsRequired: true }}
                      />
                      <Field
                        component={DropdownSelect}
                        name="dateFormat"
                        label={t('onboarding.fields.dateFormat')}
                        withFormControl
                        rules={{ required: true }}
                        innerProps={{
                          options: dateFormatOptions,
                          isMulti: false,
                          usePortal: true,
                          markAsRequired: true
                        }}
                      />
                      <Field
                        component={DropdownSelect}
                        name="preferredTimezone"
                        label={t('onboarding.fields.timeZone')}
                        withFormControl
                        rules={{ required: true }}
                        innerProps={{
                          options: timeZoneOptions,
                          isMulti: false,
                          usePortal: true,
                          markAsRequired: true
                        }}
                      />
                    </Box>
                  </Box>
                </ReadonlyProvider>
                {currentStep === ONBOARDING_STEP.FORM && (
                  <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                    <LoadingButton
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={!form.formState.isValid}
                      isLoading={isUpdatingUser}
                    >
                      {t('onboarding.saveButton')}
                    </LoadingButton>
                  </Stack>
                )}
              </Stack>
            )}
            {currentStep === ONBOARDING_STEP.FINAL && (
              <Stack
                key="final"
                gap={3}
                sx={{
                  animation: fadeInUp
                }}
              >
                <Typography variant="body1" fontWeight={typography.fontWeightMedium} color="text.primary">
                  {t('onboarding.finalStep.title')}
                </Typography>
                <Typography variant="body1" color="text.primary">
                  {t('onboarding.finalStep.subtitle1')}
                </Typography>
                <Typography variant="body1" color="text.primary">
                  {t('onboarding.finalStep.subtitle2')}
                </Typography>
                <Stack direction="row" gap={2} sx={{ mt: 2 }}>
                  <OnboardingFeatureCard
                    icon={<HierarchyChartFolderIcon width={70} height={70} />}
                    title={t('onboarding.finalStep.items.title')}
                    description={t('onboarding.finalStep.items.description')}
                  />
                  <OnboardingFeatureCard
                    icon={<SquareBriefcaseColorIcon width={70} height={70} />}
                    title={t('onboarding.finalStep.process.title')}
                    description={t('onboarding.finalStep.process.description')}
                  />
                  <OnboardingFeatureCard
                    icon={<ColoredPersonPlusIcon width={70} height={70} />}
                    title={t('onboarding.finalStep.talents.title')}
                    description={t('onboarding.finalStep.talents.description')}
                  />
                </Stack>
                <Stack gap={1.5} sx={{ mt: 2 }}>
                  <Typography variant="body1" color="text.primary">
                    {t('onboarding.finalStep.reassurance1')}
                  </Typography>
                  <Typography variant="body1" color="text.primary">
                    {t('onboarding.finalStep.reassurance2')}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="start" sx={{ mt: 3 }}>
                  <LoadingButton
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleGoHome}
                    isLoading={isActivatingUser}
                  >
                    {t('onboarding.finalStep.button')}
                  </LoadingButton>
                </Stack>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Form>
    </Stack>
  );
};

const Onboarding = () => {
  const { isAuthenticated, isSessionLoading } = useSession();
  const { isCurrentUserLoading, currentUser, preferredLocaleFromApi } = useSyncI18nWithPreferredLocale();

  if (isSessionLoading) {
    return <BackdropLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTER_PATHS.LOGIN} />;
  }

  if (isCurrentUserLoading || !currentUser?.id) {
    return <BackdropLoader />;
  }

  if (currentUser.status !== GeneralStatusEnum.ONBOARDING) {
    return <Navigate to={ROUTER_PATHS.HOME} />;
  }

  const initialLanguage = preferredLocaleFromApi ?? EN_US_LOCALE;

  return <OnboardingFlow initialLanguage={initialLanguage} currentUser={currentUser} />;
};

export default Onboarding;
